// 📁 src/lightversion/Day7Lite.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Alert,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosInstance"; // ✅ already configured
import { useLite } from "./context/LiteContext";
import LiteDayGate from "./LiteDayGate";
import LiteDayLayout from "./LiteDayLayout";
import ExperienceItem from "./components/ExperienceItem";

/* --------------------------------------------------
   🔐 CONSTANTS
-------------------------------------------------- */

const PRO_INTEREST_ENDPOINT = "/lite/pro-interest"; // ✅ canonical lite backend path

/* --------------------------------------------------
   📊 SAFE ANALYTICS HOOK
-------------------------------------------------- */
const track = (event, payload = {}) => {
  if (typeof window !== "undefined" && window.mdihTrack) {
    window.mdihTrack(event, payload);
  }
};

/* --------------------------------------------------
   🧪 SAFE SESSION ID (ANONYMOUS, STABLE)
-------------------------------------------------- */
const getLiteSessionId = () => {
  let id = localStorage.getItem("lite_session_id");
  if (!id) {
    id = `lite_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("lite_session_id", id);
  }
  return id;
};

const Day7Lite = () => {
  const navigate = useNavigate();
  const { 
  liteJourney,
  markDayComplete,
  startFreshDay,
  entryDecided,
  resetEntryDecision,
  loading: liteLoading
  } = useLite();

  const [decision, setDecision] = useState(null);

  /* --------------------------------------------------
   🔒 ENTRY DECISION GUARD
-------------------------------------------------- */
useEffect(() => {

  if (liteLoading) return;

  if (!entryDecided && window.location.pathname !== "/lite/welcome") {
    navigate("/lite/welcome",{replace:true});
  }

 }, [entryDecided, liteLoading, navigate]);

  /* =====================================================
     🧠 DAY 7 VIEW TRACKING
  ===================================================== */
  useEffect(() => {
    track("lite_day7_viewed", {
      purpose: liteJourney?.purpose?.statement,
      hasDirection: !!liteJourney?.direction,
    });
  }, [liteJourney]);

  /* =====================================================
     🧠 PERSIST DECISION (LOCAL TERMINAL STATE)
  ===================================================== */
  useEffect(() => {
    if (!decision) return;

    const updated = {
      ...liteJourney,
      decisionDaySeen: true,
      decision,
      decidedAt: Date.now(),
    };

    localStorage.setItem("lite_journey", JSON.stringify(updated));
    track("lite_day7_decision", { decision });
  }, [decision, liteJourney]);

  /* =====================================================
     🚀 PRO INTEREST CAPTURE (NON-BLOCKING, SAFE)
  ===================================================== */
  const recordProInterest = async () => {
    try {
      await axios.post(PRO_INTEREST_ENDPOINT, {
        liteSessionId: getLiteSessionId(),
        source: "day7_lite",
        interestType: "early_access",
        decision: "go-pro",
        purpose: liteJourney?.purpose?.statement || null,
        direction: liteJourney?.direction?.title || null,
      });
    } catch (err) {
      // ❗ Silent by design — Lite UX must NEVER break
      console.warn("⚠️ ProInterest capture failed (safe to ignore)");
    }
  };

  return (
    <LiteDayGate allow={["ELIGIBLE_FOR_PRO", "SOFT_REVEAL"]}>
      <LiteDayLayout day={7}>
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          {/* HEADER */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Chip label="Day 7 • Reflection & Choice" sx={{ mb: 2 }} />

            <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
              This is a meaningful pause
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 4 }}>
              You didn’t rush to build. You chose to understand the problem first.
            </Typography>
          </motion.div>

          {/* INTRO */}
          <Box sx={{ mb: 6 }}>
            <Stack spacing={4}>
              <ExperienceItem
                icon="🧭"
                title="Acknowledge what you’ve done"
                text="You clarified a problem, explored directions, and tested real-world relevance."
              />
              <ExperienceItem
                icon="🧠"
                title="Decide without pressure"
                text="There is no right answer — only what fits you right now."
              />
              <ExperienceItem
                icon="🌱"
                title="Carry clarity forward"
                text="Clarity compounds over time. You now think like a builder."
              />
            </Stack>
          </Box>

          {/* SUMMARY */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="subtitle2">
                  Your 7-day exploration
                </Typography>

                <Typography variant="body2">
                  <strong>Purpose:</strong>{" "}
                  {liteJourney?.purpose?.statement || "—"}
                </Typography>

                {liteJourney?.direction && (
                  <Typography variant="body2">
                    <strong>Direction:</strong>{" "}
                    {liteJourney.direction.title}
                  </Typography>
                )}

                <Typography variant="body2">
                  <strong>Market relevance explored:</strong> Yes
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* DECISION OPTIONS */}
          <Stack spacing={3}>
            {/* PAUSE */}
            <Card>
              <CardContent>
                <Typography fontWeight="bold" sx={{ mb: 1 }}>
                  I want to pause and reflect
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  This problem feels important, but I want more time to think.
                </Typography>

                <Button
                  variant="outlined"
                  onClick={() => {
                    setDecision("pause");
                    markDayComplete();
                    navigate("/lite/learning");
                  }}
                >
                  Stay in Lite →
                </Button>
              </CardContent>
            </Card>

            {/* GO PRO */}
            <Card
              sx={{
                border: "2px solid #1976d2",
                backgroundColor: "#f5faff",
              }}
            >
              <CardContent>
                <Typography fontWeight="bold" sx={{ mb: 1 }}>
                  I want to pursue this seriously
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  I want structure, tools, and guidance to move from clarity to
                  execution.
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  onClick={async () => {
                    setDecision("go-pro");
                    await recordProInterest(); // ✅ THIS WAS THE MISSING PIECE
                    track("lite_pro_intent");
                    markDayComplete();
                    navigate("/lite/pro-coming-soon");
                  }}
                >
                  See Pro tools & pricing →
                </Button>
              </CardContent>
            </Card>

            {/* RESTART */}
            <Card>
              <CardContent>
                <Typography fontWeight="bold" sx={{ mb: 1 }}>
                  Restart with a different problem
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  This exploration helped — but I want to rethink what I work on.
                </Typography>

                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => {
                    setDecision("restart");
                    resetEntryDecision();
                    startFreshDay(1);
                    navigate("/lite/mentor");
                  }}
                >
                  Restart journey
                </Button>
              </CardContent>
            </Card>
          </Stack>

          <Divider sx={{ my: 5 }} />

          <Alert severity="info">
            <Typography variant="body2">
              You didn’t waste time. You built clarity — and that’s rare.
            </Typography>
          </Alert>
        </Box>
      </LiteDayLayout>
    </LiteDayGate>
  );
};

export default Day7Lite;
